import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  patientRelationsApi,
  patientRelationQueryKeys,
  RELATIONSHIP_LABEL,
  type RelationshipCode,
} from "../../../shared/api/patientRelations";
import { patientsApi } from "../../../shared/api/patients";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const REL_OPTIONS: RelationshipCode[] = [
  "CHILD",
  "SPOUSE",
  "PARENT",
  "SIBLING",
  "GRANDPARENT",
  "GRANDCHILD",
  "OTHER",
];

export function PatientRelationsSection({ patientId }: { patientId: string | undefined }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pid = patientId ?? "";

  const relatives = useQuery({
    queryKey: patientRelationQueryKeys.list(pid),
    queryFn: () => patientRelationsApi.list(pid),
    enabled: !!patientId,
  });

  const [mode, setMode] = useState<"link" | "new">("link");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [rel, setRel] = useState<RelationshipCode>("CHILD");
  const [error, setError] = useState<string | null>(null);
  // Tạo người thân mới
  const [nfName, setNfName] = useState("");
  const [nfGender, setNfGender] = useState("");
  const [nfDob, setNfDob] = useState("");
  const [nfPhone, setNfPhone] = useState("");

  const search = useQuery({
    queryKey: ["patient-search-rel", q],
    queryFn: () => patientsApi.list({ q }, { size: 8, sort: "fullName" }),
    enabled: q.trim().length >= 2,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: patientRelationQueryKeys.list(pid) });

  const add = useMutation({
    mutationFn: () => patientRelationsApi.add(pid, picked!.id, rel),
    onSuccess: () => {
      invalidate();
      setPicked(null);
      setQ("");
      setError(null);
    },
    onError: (e) => setError(apiError(e).detail),
  });
  const remove = useMutation({
    mutationFn: (relationId: string) => patientRelationsApi.remove(pid, relationId),
    onSuccess: invalidate,
  });
  const createNew = useMutation({
    mutationFn: async () => {
      const res = await patientsApi.create({
        fullName: nfName.trim(),
        gender: (nfGender || null) as never,
        dateOfBirth: nfDob || null,
        phone: nfPhone.trim() || null,
        forceDuplicate: true,
      });
      await patientRelationsApi.add(pid, res.patient.id, rel);
    },
    onSuccess: () => {
      invalidate();
      setNfName("");
      setNfGender("");
      setNfDob("");
      setNfPhone("");
      setError(null);
    },
    onError: (e) => setError(apiError(e).detail),
  });

  if (!patientId) return null;
  const list = relatives.data ?? [];

  return (
    <div className="grid gap-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-2 text-[15px] font-semibold">Người thân</h3>
          {relatives.isLoading ? (
            <p>Đang tải…</p>
          ) : list.length === 0 ? (
            <p className="text-muted-foreground">
              Chưa liên kết người thân nào. Liên kết để đặt lịch hộ nhanh hơn.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Họ &amp; Tên</TableHead>
                  <TableHead>Quan hệ</TableHead>
                  <TableHead>Mã BN</TableHead>
                  <TableHead>SĐT</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((r) => (
                  <TableRow key={r.relationId}>
                    <TableCell>
                      <Link
                        to={`/patients/${r.relativePatientId}`}
                        className="text-primary hover:underline"
                      >
                        {r.fullName}
                      </Link>
                    </TableCell>
                    <TableCell>{RELATIONSHIP_LABEL[r.relationship] ?? r.relationship}</TableCell>
                    <TableCell>{r.patientCode ?? "—"}</TableCell>
                    <TableCell>{r.phone ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          navigate(`/appointments/new?patientId=${r.relativePatientId}`)
                        }
                      >
                        Đặt lịch
                      </Button>{" "}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove.mutate(r.relationId)}
                      >
                        Gỡ
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-2.5 p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[15px] font-semibold">Thêm người thân</h3>
            <div className="flex gap-1.5">
              <Button
                type="button"
                size="sm"
                variant={mode === "link" ? "default" : "ghost"}
                onClick={() => setMode("link")}
              >
                Liên kết có sẵn
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === "new" ? "default" : "ghost"}
                onClick={() => setMode("new")}
              >
                Tạo mới
              </Button>
            </div>
          </div>

          {mode === "new" ? (
            <div className="flex flex-wrap items-end gap-2.5">
              <div className="flex min-w-[160px] flex-1 flex-col gap-1.5">
                <Label htmlFor="rel-nf-name">Họ &amp; Tên</Label>
                <Input
                  id="rel-nf-name"
                  type="text"
                  value={nfName}
                  onChange={(e) => setNfName(e.target.value)}
                  placeholder="VD: Nguyễn Bảo An"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rel-nf-gender">Giới tính</Label>
                <Select value={nfGender || undefined} onValueChange={(v) => setNfGender(v)}>
                  <SelectTrigger id="rel-nf-gender" className="w-[110px]">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Nam</SelectItem>
                    <SelectItem value="F">Nữ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rel-nf-dob">Ngày sinh</Label>
                <Input
                  id="rel-nf-dob"
                  type="date"
                  value={nfDob}
                  onChange={(e) => setNfDob(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rel-nf-phone">SĐT</Label>
                <Input
                  id="rel-nf-phone"
                  type="tel"
                  value={nfPhone}
                  onChange={(e) => setNfPhone(e.target.value)}
                  placeholder="tuỳ chọn"
                  className="w-[130px]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rel-nf-rel">Quan hệ</Label>
                <Select
                  value={rel}
                  onValueChange={(v) => setRel(v as RelationshipCode)}
                >
                  <SelectTrigger id="rel-nf-rel" className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REL_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {RELATIONSHIP_LABEL[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={!nfName.trim() || createNew.isPending}
                onClick={() => createNew.mutate()}
              >
                Tạo &amp; liên kết
              </Button>
            </div>
          ) : picked ? (
            <div className="flex flex-wrap items-center gap-2.5">
              <span>
                Đã chọn: <strong>{picked.name}</strong>
              </span>
              <Select value={rel} onValueChange={(v) => setRel(v as RelationshipCode)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REL_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {RELATIONSHIP_LABEL[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                disabled={add.isPending}
                onClick={() => add.mutate()}
              >
                Liên kết
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPicked(null)}
              >
                Chọn lại
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Tìm bệnh nhân theo tên / SĐT / mã BN…"
                  className="pl-9"
                />
              </div>
              {q.trim().length >= 2 && (
                <div className="grid gap-1">
                  {(search.data?.content ?? [])
                    .filter((p) => p.id !== patientId)
                    .map((p) => (
                      <Button
                        key={p.id}
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="justify-start"
                        onClick={() => setPicked({ id: p.id, name: p.fullName })}
                      >
                        {p.fullName} · {p.patientCode ?? ""}{" "}
                        {p.phoneE164 ? `· ${p.phoneE164}` : ""}
                      </Button>
                    ))}
                  {search.data && search.data.content.length === 0 && (
                    <span className="text-[13px] text-muted-foreground">
                      Không tìm thấy. Người thân chưa có hồ sơ? Hãy tạo bệnh nhân mới rồi liên
                      kết.
                    </span>
                  )}
                </div>
              )}
            </>
          )}
          {error && <p className="text-destructive">{error}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
